import React from 'react'
import { ExternalLinkIcon, MapPinIcon, XIcon } from 'lucide-react'
import type { JobAttachment } from '../../api/jobWorkflow'
import {
  formatAccuracyM,
  formatCoordinates,
  googleMapsUrl,
} from '../../lib/geoDisplay'

export type AttachmentDetail = JobAttachment & {
  employee?: { first_name?: string | null; last_name?: string | null } | null
  job_task?: { label?: string | null } | null
}

function empName(e?: { first_name?: string | null; last_name?: string | null } | null) {
  if (!e) return null
  const name = `${e.first_name || ''} ${e.last_name || ''}`.trim()
  return name || null
}

function fmt(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="col-span-2 text-sm text-gray-900 break-words">{children}</dd>
    </div>
  )
}

interface AttachmentDetailModalProps {
  attachment: AttachmentDetail | null
  onClose: () => void
}

export function AttachmentDetailModal({ attachment, onClose }: AttachmentDetailModalProps) {
  if (!attachment) return null

  const meta = (attachment.metadata || {}) as Record<string, unknown>
  const maps = googleMapsUrl(attachment.latitude, attachment.longitude)
  const gpsError = typeof meta.gps_error === 'string' ? meta.gps_error : null
  const captureSource =
    attachment.capture_source ||
    (typeof meta.capture_source === 'string' ? meta.capture_source : null)
  const taskLabel =
    attachment.job_task?.label ||
    (typeof meta.job_task_label === 'string' ? meta.job_task_label : null)
  const who = empName(attachment.employee)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Attachment details</h2>
            <p className="text-sm text-gray-500 truncate max-w-[16rem]">
              {attachment.caption || attachment.file_name || 'File'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {attachment.mime_type?.startsWith('image/') && (
          <div className="bg-gray-100">
            <img
              src={attachment.file_url}
              alt={attachment.caption || attachment.file_name || ''}
              className="w-full max-h-64 object-contain"
            />
          </div>
        )}

        <div className="px-5 py-4 space-y-1">
          <Row label="Captured">{fmt(attachment.captured_at || attachment.created_at)}</Row>
          <Row label="Source">{captureSource || '—'}</Row>
          <Row label="Checklist task">{taskLabel || (attachment.job_task_id ? 'Linked task' : '—')}</Row>
          <Row label="Employee">{who || '—'}</Row>
          <Row label="Type">{attachment.attachment_type || '—'}</Row>

          <div className="pt-3 pb-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
              Capture location
            </p>
            <Row label="GPS">
              <span className="inline-flex items-center gap-2 flex-wrap">
                <MapPinIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {formatCoordinates(attachment.latitude, attachment.longitude)}
                {maps && (
                  <a
                    href={maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-blue-600 hover:underline text-xs"
                  >
                    Map <ExternalLinkIcon className="w-3 h-3" />
                  </a>
                )}
              </span>
            </Row>
            <Row label="Accuracy">{formatAccuracyM(attachment.accuracy_m)}</Row>
            {gpsError && (
              <Row label="GPS note">
                <span className="text-amber-700">{gpsError}</span>
              </Row>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
          <a
            href={attachment.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Open file
          </a>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
